import { useMemo, useState } from 'react'
import { Combobox } from '@headlessui/react'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core'
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { CSS } from '@dnd-kit/utilities'
import {
    ArrowDownIcon,
    ArrowUpIcon,
    Bars3Icon,
    MagnifyingGlassIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import {
    Article,
    unwrapResponse,
    useGetArticleData,
    useGetArticleDetails,
} from 'hooks/article'
import { useDebounce } from 'hooks/useDebounce'

const MAX_RELATED = 20

const RelatedArticleRow = ({
    id,
    title: knownTitle,
    index,
    total,
    disabled,
    onRemove,
    onMove,
}: {
    id: number
    title?: string
    index: number
    total: number
    disabled?: boolean
    onRemove: () => void
    onMove: (offset: number) => void
}) => {
    // Saved articles and AI suggestions contain IDs; resolve their titles for display.
    const details = useGetArticleDetails(id, {
        enabled: !knownTitle,
        staleTime: 300000,
        cacheTime: 300000,
    })
    const article = unwrapResponse(details.data) as Article | undefined
    const title = knownTitle || article?.title || `Article #${id}`
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled })
    const actionClass =
        'rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 dark:hover:bg-slate-700 dark:hover:text-slate-200'

    return (
        <li
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                position: 'relative',
                zIndex: isDragging ? 30 : undefined,
            }}
            className={`flex items-center gap-2 rounded-lg border bg-white p-2.5 dark:bg-slate-800 ${
                isDragging
                    ? 'border-blue-400 shadow-lg'
                    : 'border-slate-200 dark:border-slate-600'
            }`}
        >
            <button
                type="button"
                ref={setActivatorNodeRef}
                {...attributes}
                {...listeners}
                disabled={disabled}
                aria-label={`Drag ${title} to reorder`}
                title="Drag to reorder, or press Space and use the arrow keys"
                className="touch-none rounded p-1 text-slate-400 enabled:cursor-grab enabled:active:cursor-grabbing disabled:opacity-30"
            >
                <Bars3Icon className="h-4 w-4" aria-hidden="true" />
            </button>
            <span className="text-[10px] tabular-nums text-slate-400">
                {index + 1}
            </span>
            <div className="min-w-0 flex-1">
                <p className="break-words text-xs font-medium leading-5 text-slate-700 dark:text-slate-200">
                    {title}
                </p>
                {!knownTitle && details.isLoading && (
                    <p className="text-[10px] text-slate-400">Loading title…</p>
                )}
                {!knownTitle && details.isError && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400">
                        Title unavailable · Selection retained
                    </p>
                )}
            </div>
            <div className="flex shrink-0 items-center">
                <div className="flex flex-col">
                    <button
                        type="button"
                        disabled={disabled || index === 0}
                        onClick={() => onMove(-1)}
                        aria-label={`Move ${title} up`}
                        className={actionClass}
                    >
                        <ArrowUpIcon className="h-3 w-3" />
                    </button>
                    <button
                        type="button"
                        disabled={disabled || index === total - 1}
                        onClick={() => onMove(1)}
                        aria-label={`Move ${title} down`}
                        className={actionClass}
                    >
                        <ArrowDownIcon className="h-3 w-3" />
                    </button>
                </div>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={onRemove}
                    aria-label={`Remove ${title}`}
                    className={actionClass}
                >
                    <XMarkIcon className="h-4 w-4" />
                </button>
            </div>
        </li>
    )
}

const ArticleRelatedArticles = ({
    articleId,
    control,
    setValue,
    disabled,
    error,
}: {
    articleId?: number
    control: Control<any>
    setValue: UseFormSetValue<any>
    disabled?: boolean
    error?: string
}) => {
    const value: string = useWatch({ control, name: 'relatedArticles' }) || ''
    const ids = useMemo(
        () =>
            Array.from(
                new Set(
                    value
                        .split(',')
                        .map((part) => Number(part.trim()))
                        .filter((id) => Number.isSafeInteger(id) && id > 0)
                )
            ),
        [value]
    )
    const [search, setSearch] = useState('')
    const [titles, setTitles] = useState<Record<number, string>>({})
    const debouncedSearch = useDebounce(search.trim(), 400)
    const query = useGetArticleData(
        {
            search: debouncedSearch,
            status: 'published',
            pagination: { pageIndex: 0, pageSize: 20 },
        },
        {
            enabled: !disabled && Boolean(debouncedSearch),
            keepPreviousData: true,
        }
    )
    const results: Article[] = query.data?.data?.results ?? []
    const searching = search.trim() !== debouncedSearch || query.isFetching
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )
    const update = (next: number[]) => {
        if (disabled) return
        // Keep the existing form/API mapping; array order is preserved on save.
        setValue('relatedArticles', next.join(', '), {
            shouldDirty: true,
            shouldValidate: true,
        })
    }
    const addArticle = (article: Article | null) => {
        if (!article || disabled) return
        const id = Number(article.id)
        if (
            !Number.isSafeInteger(id) ||
            id <= 0 ||
            id === articleId ||
            ids.includes(id) ||
            ids.length >= MAX_RELATED
        )
            return
        setTitles((previous) => ({ ...previous, [id]: article.title }))
        update([...ids, id])
        setSearch('')
    }
    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) return
        const from = ids.indexOf(Number(active.id))
        const to = ids.indexOf(Number(over.id))
        if (from >= 0 && to >= 0) update(arrayMove(ids, from, to))
    }

    return (
        <div>
            <Combobox
                value={null}
                onChange={addArticle}
                disabled={disabled || ids.length >= MAX_RELATED}
            >
                <Combobox.Label className="sr-only">
                    Search related articles
                </Combobox.Label>
                <div className="relative">
                    <MagnifyingGlassIcon
                        className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400"
                        aria-hidden="true"
                    />
                    <Combobox.Input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search by article title…"
                        className="w-full py-2 pl-9 pr-3"
                        aria-describedby="related-articles-help"
                    />
                    <Combobox.Options className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg focus:outline-none dark:border-slate-600 dark:bg-slate-800">
                        {!search.trim() ? (
                            <li
                                role="presentation"
                                className="px-3 py-2 text-xs text-slate-400"
                            >
                                Type a title to search published articles.
                            </li>
                        ) : searching ? (
                            <li
                                role="presentation"
                                className="px-3 py-2 text-xs text-slate-400"
                            >
                                Searching…
                            </li>
                        ) : query.isError ? (
                            <li
                                role="presentation"
                                className="px-3 py-2 text-xs text-red-500"
                            >
                                Could not load articles. Try searching again.
                            </li>
                        ) : results.length === 0 ? (
                            <li
                                role="presentation"
                                className="px-3 py-2 text-xs text-slate-400"
                            >
                                No articles found.
                            </li>
                        ) : (
                            results.map((article) => {
                                const added = ids.includes(Number(article.id))
                                const isCurrent =
                                    Number(article.id) === articleId
                                return (
                                    <Combobox.Option
                                        key={article.id}
                                        value={article}
                                        disabled={added || isCurrent}
                                        className={({ active }) =>
                                            `flex items-center justify-between gap-2 px-3 py-2 text-xs ${
                                                added || isCurrent
                                                    ? 'cursor-not-allowed opacity-50'
                                                    : 'cursor-pointer'
                                            } ${
                                                active
                                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200'
                                                    : 'text-slate-700 dark:text-slate-200'
                                            }`
                                        }
                                    >
                                        <span className="min-w-0 break-words">
                                            {article.title}
                                        </span>
                                        {(added || isCurrent) && (
                                            <span className="shrink-0 text-[10px]">
                                                {added
                                                    ? 'Added'
                                                    : 'This article'}
                                            </span>
                                        )}
                                    </Combobox.Option>
                                )
                            })
                        )}
                    </Combobox.Options>
                </div>
            </Combobox>
            <div
                id="related-articles-help"
                className="my-3 flex justify-between gap-2 text-xs text-slate-400"
            >
                <span>
                    {ids.length >= MAX_RELATED
                        ? 'Maximum reached. Remove an article to add another.'
                        : 'Drag to reorder, or use the arrows.'}
                </span>
                <span className="shrink-0 tabular-nums">
                    {ids.length}/{MAX_RELATED}
                </span>
            </div>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={onDragEnd}
            >
                <SortableContext
                    items={ids}
                    strategy={verticalListSortingStrategy}
                >
                    <ol
                        className="space-y-2"
                        aria-label="Selected related articles"
                    >
                        {ids.map((id, index) => (
                            <RelatedArticleRow
                                key={id}
                                id={id}
                                title={titles[id]}
                                index={index}
                                total={ids.length}
                                disabled={disabled}
                                onRemove={() =>
                                    update(ids.filter((item) => item !== id))
                                }
                                onMove={(offset) => {
                                    const to = index + offset
                                    if (to >= 0 && to < ids.length)
                                        update(arrayMove(ids, index, to))
                                }}
                            />
                        ))}
                    </ol>
                </SortableContext>
            </DndContext>
            {!ids.length && (
                <p className="rounded-lg border border-dashed border-slate-200 px-3 py-5 text-center text-xs text-slate-400 dark:border-slate-600">
                    No related articles selected yet.
                </p>
            )}
            {error && (
                <p role="alert" className="mt-2 text-xs text-red-500">
                    {error}
                </p>
            )}
        </div>
    )
}

export default ArticleRelatedArticles
